<script setup>
import { onMounted, ref, toRefs, toRaw, watch } from 'vue';
import Main from './hoc/main.vue';
import translate from './utility/translate';
import isFullUrl from './utility/url';

const props = defineProps({
  jsonUrl: {
    type: String,
    required: true,
    default() {
      return '';
    },
  },
});

const resetFilters = {
  function: null,
  country: null,
  location: null,
  workType: null,
  seniorityLevel: null,
};

const filterValues = ref(resetFilters);

const selectedCountry = ref(null);

const selectedLocation = ref(null);

const { jsonUrl } = toRefs(props);

const searchParamsExists = ref(false);

const dataLayerState = ref({
  "event": "ga4_job_opportunities_filter",
  "ga4_data": {
    "active_filter_choice": "",
  },
});

const getQueryStringValue = (string, prop) => {
  const selected = essity.filters.workday[prop]
    .find((record) => {
      return record.id === string;
    });

  if (selected) {
    return selected;
  }
  return null;
};

const processQueryParams = (searchParams, filterProps) => {
  filterProps.forEach((prop) => {
    const value = searchParams.get(prop);
    if (value) {
      filterValues.value[prop] = {
        ...getQueryStringValue(value, prop),
      };
    }
  });
};

const updateFilterLocation = (response) => {
  if (selectedCountry.value) {
    const { countryLocations } = response;
    const list = countryLocations[selectedCountry.value];
    if (list) {
      let formated = list.reduce((acc, location) => {
        const { locationId } = location;
        if (locationId === selectedLocation.value) {
          return [...acc, {
            id: location.locationId,
            text: location.location,
            selected: true,
          }]
        }
        return [...acc, {
          id: location.locationId,
          text: location.location,
        }];
      }, []);
      formated = [
        {
          "id": "all",
          "text": "Location (all)"
        },
        ...formated
      ];
      return formated;
    }
  }
  return essity.filters.workday?.location;
}

const dataLayerAction = (property, value) => {
  const choice = dataLayerState.value.ga4_data.active_filter_choice;
  const regex = new RegExp(`${property} \\[.*?\\]`);

  let updatedChoice;
  if (value === "all") {
    updatedChoice = choice.replace(regex, "").trim();
    updatedChoice = updatedChoice.endsWith(",")
      ? updatedChoice.slice(0, -1).trim()
      : updatedChoice;
  } else {
    const newProperty = `${property} [${value}]`;
    updatedChoice = choice.match(regex)
      ? choice.replace(regex, newProperty)
      : (choice ? `${choice}, ` : "") + newProperty.trim();
  }

  dataLayerState.value = {
    "event": "ga4_job_opportunities_filter",
    "ga4_data": {"active_filter_choice": updatedChoice},
  };

  const dataLayerObject = toRaw(dataLayerState.value);

  window?.dataLayer.push(dataLayerObject);
}

const windowLocationSet = () => {
  const params = new URLSearchParams();

  Object.entries(filterValues.value).forEach(([key, value]) => {
    if (value && typeof value === 'object' && 'id' in value) {
      params.set(key, value.id);
    } else if (value) {
      params.set(key, value);
    }
  });

  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState(null, '', newUrl);
};

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const filterProps = ['function', 'country', 'location', 'workType', 'seniorityLevel'];
  let urlObj = null;
  if (urlParams.size) {
    searchParamsExists.value = true;
    processQueryParams(urlParams, filterProps);
  }
  if (isFullUrl(jsonUrl.value)) {
    urlObj = new URL(jsonUrl.value);
  } else {
    urlObj = new URL(jsonUrl.value, window.location.origin);
  }

  const urlObjParams = urlObj.searchParams;
  if (urlObjParams.size) {
    searchParamsExists.value = true;
    const params = new URLSearchParams(urlObj.search);
    const allParams = Array.from(params.entries()).reduce((acc, [key]) => {
      acc[key] = params.get(key);
      return acc;
    }, {});

    filterValues.value = {
      ...filterValues.value,
      ...allParams,
    }

    processQueryParams(urlObjParams, filterProps);
  }
});

watch(filterValues, () => {
  windowLocationSet();
}, { deep: true });
</script>

<template>
  <Main :endpoint="props.jsonUrl" :filter-obj="{
    query: '',
    function: '',
    country: '',
    location: '',
    workType: '',
    seniorityLevel: ''
  }" :paginate="true" :hit-by-page="10">
    <template #filters="{ onFilterChanged, listItems, firstLoad, restFromResponse }">
      <section v-if="firstLoad || listItems?.length" class="section">
        <div class="workday-filters">
          <div class="InputWithIcon InputWithIcon--alignRight InputWithIcon--iconLarge">
            <i class="Icon Icon-search" aria-hidden="true" />
            <input id="workday-query" name="query" class="form-control" type="search" :value="filterValues.query"
              :placeholder="essity.translations.workday.searchPlaceholder" @keyup="($event) => {
                const { value } = $event.target
                filterValues.query = value;
                return onFilterChanged({ query: value });
              }" @click="clearQueryFilter">
          </div>
          <div class="mt-16">
            <toggle-button
              targetId="workday-filter-list"
              :isOpen="searchParamsExists"
              :aria-controls="'workday-filter-list'"
              :aria-label="`${essity?.translations?.workday?.filterToggleButton} ${searchParamsExists ? essity.translations.workday.close : essity.translations.workday.open}`"
            >
              <i class="i i-filter txt-secondary"></i>
              <template #text>
                <span class="txt-secondary">{{ essity.translations.workday.filterResultsPlaceholder }}</span>
              </template>
            </toggle-button>
          </div>
          <div id="workday-filter-list" :class="{ hidden: !searchParamsExists }">
            <div class="workday-filter-group flex">
              <div class="workday-filter">
                <Select2 id="workday-function-001" :default-option-selected="filterValues.function"
                  :options="essity.filters.workday?.function || []"
                  :placeholder="essity.filters.workday?.function[0].text" name="workday-function-001" @selecthandler="($event) => {
                    const value = $event.id == 'all' ? 'all' : $event.text;
                    dataLayerAction('function', value);
                    filterValues.function = $event.id;
                    return onFilterChanged({ function: $event.id });
                  }" />
              </div>
              <div class="workday-filter">
                <Select2 id="workday-country-001" :default-option-selected="filterValues.country"
                  custom-class="input-enable" :options="essity.filters.workday?.country || []"
                  :placeholder="essity.filters.workday?.country[0].text" name="workday-country-001" @selecthandler="($event) => {
                    selectedCountry = $event.id;
                    selectedLocation = null;
                    const value = $event.id == 'all' ? 'all' : $event.text;
                    dataLayerAction('country', value);
                    filterValues.country = $event.id;
                    filterValues.location = null;
                    return onFilterChanged({ country: $event.id, location: null });
                  }" />
              </div>
            </div>
            <div class="workday-filter-group flex">
              <div class="workday-filter">
                <Select2 id="workday-loacation-001" v-model="selectedLocation"
                  :default-option-selected="filterValues.location"
                  :options="updateFilterLocation(restFromResponse) || []"
                  :placeholder="essity.filters.workday?.location[0].text" custom-class="input-enable"
                  name="workday-location-001" @selecthandler="($event) => {
                    selectedLocation = $event.id;
                    const value = $event.id == 'all' ? 'all' : $event.text;
                    dataLayerAction('location', value);
                    filterValues.location = $event.id;
                    return onFilterChanged({ location: $event.id });
                  }" />
              </div>
              <div class="workday-filter">
                <Select2 id="workday-work-type-001" :default-option-selected="filterValues.workType"
                  :options="essity.filters.workday?.workType || []"
                  :placeholder="essity.filters.workday?.workType[0].text" name="workday-work-type-001" @selecthandler="($event) => {
                    const value = $event.id == 'all' ? 'all' : $event.text;
                    dataLayerAction('workType', value);
                    filterValues.workType = value;
                    return onFilterChanged({ workType: $event.id });
                  }" />
              </div>
              <div class="workday-filter">
                <Select2 id="workday-seniority-level-001" :default-option-selected="filterValues.seniorityLevel"
                  :options="essity.filters.workday?.seniorityLevel || []"
                  :placeholder="essity.filters.workday?.seniorityLevel[0].text" name="workday-seniority-level-001"
                  @selecthandler="($event) => {
                    const value = $event.id == 'all' ? 'all' : $event.text;
                    dataLayerAction('seniorityLevel', value);
                    filterValues.seniorityLevel = value;
                    return onFilterChanged({ seniorityLevel: $event.id });
                  }" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
    <template #default="{ listItems, isLoading }">
      <div :class="['result-list', { 'is-loading': isLoading }]">
        <div v-for="item of listItems" :key="item" class="workday-item">
          <div class="result-item">
            <h5 class="no-margin">
              <a :href="item.url" class="txt-primary">
                <span class="title-txt">{{ `${item.title}` }}</span>
              </a>
            </h5>
            <p class="item-body-text">
              {{ item.remoteType }}
            </p>
            <p v-if="item.seniorityLevel" class="no-margin">{{ item.seniorityLevel }}</p>
          </div>
          <div class="workday-item-content">
            <div class="workday-item-column">
              <p class="txt-primary mb-8 mt-4">
                <strong>
                  {{ essity.translations.workday.location }}:
                </strong>
              </p>
              <p v-if="item.primaryLocation && !item.additionalLocations" class="small">
                {{ item.primaryLocation }}
              </p>
              <p v-if="item.primaryLocation && item.additionalLocations" class="small no-margin">
                {{
                  translate(
                    essity.translations.workday.locations,
                    {
                      // adding primary location + 1
                      locationNumber: item.additionalLocations.split(';').length + 1
                    }
                  )
                }}
              </p>
            </div>
            <div class="workday-item-column">
              <p class="txt-primary strong txt-primary mb-8 mt-4">
                <strong>
                  {{ essity.translations.workday.jobFunction }}:
                </strong>
              </p>
              <p v-for="job of item.jobFamilyGroup_group" :key="job.jobFamilyGroup_WID" class="small no-margin">
                {{ job.jobFamilyGroup }}
              </p>
            </div>
            <div class="workday-item-column flex">
              <a
                :href="item?.url"
                class="btn bg-primary mt-4 btn--small work-item-button"
                target="_blank"
                :aria-label="`${essity.translations.workday.buttonText} ${essity?.translations.files.ariaLabelOpenNewTab}`"
              >
                <small>{{ essity.translations.workday.buttonText }}</small>
              </a>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Main>
</template>
